// @flow

import {unsafelyParseMarkdown, isMarkdocAstTag, Tag} from 'docs/util';

import type {MarkdocTagRegistration} from 'docs/markdoc';

function parseMarkdown(string, config) {
  // If we tried to convert Markdown to HTML on the Ruby side then we would output
  // HTML with different styles than when we do it here. For example, `hello` gets converted
  // to the html <code>hello</code> when generated with regular Markdown, but with Markdoc
  // it becomes <InlineCode>hello</InlineCode> which ensures it is the same style.
  const tag = unsafelyParseMarkdown(string, config);
  // Markdoc.process returns a document, we only want the first paragraph element
  // to render as a child
  return isMarkdocAstTag(tag) ? tag.children[0] : '';
}

// These Markdoc tags require data from the reports Markdoc context

export const FinanceReportSchemas: MarkdocTagRegistration = {
  tag: 'finance-report-schemas',
  component: 'FinanceReportSchemas',
  attributes: {
    reportsMap: {
      type: Object,
      description: 'Pass in the report map object from a context',
    },
    keys: {
      type: Array,
      description: 'The keys to pick',
    },
    showTableOfContents: {
      type: Boolean,
      default: false,
    },
    reportGroupsMap: {
      type: Object,
      required: false,
      description: 'If you are passing in a key of report groups pass this in',
    },
    reportGroupsKeys: {
      type: Array,
      description: 'The report groups keys',
    },
  },
  render(node, config) {
    const children = node.renderChildren(config);
    const attributes = node.renderAttributes(this.attributes, config);

    if (attributes.reportsMap) {
      const reportsMap = attributes.reportsMap;
      Object.keys(reportsMap).forEach((token) => {
        reportsMap[token].columns = reportsMap[token].columns.map((col) => {
          col.description = parseMarkdown(col.raw_markdown_description, config);
          return col;
        });
      });

      attributes.reportsMap = reportsMap;
    }

    if (attributes.reportGroupsMap) {
      const reportsMap = attributes.reportGroupsMap;
      Object.keys(reportsMap).forEach((groupID) => {
        const reportsInGroup = reportsMap[groupID];
        if (!reportsInGroup) {
          // Make sure that the report group is declared in the reports context
          throw new Error(`Report group not found: ${groupID}`);
        }

        reportsInGroup.forEach((report) => {
          report.columns = report.columns.map((col) => {
            col.description = parseMarkdown(
              col.raw_markdown_description,
              config,
            );
            return col;
          });
        });
      });

      attributes.reportGroupsMap = reportsMap;
    }

    return new Tag(this.tag, attributes, children);
  },
};

export const FinanceReportParameters: MarkdocTagRegistration = {
  tag: 'finance-report-parameters',
  component: 'FinanceReportParameters',
  attributes: {
    reportGroupsMap: {
      type: Object,
      required: true,
      description:
        'Pass in the report map parameters object from the reports context',
    },
    keys: {
      type: Array,
      required: true,
      description: 'The keys to pick from the report parameters map',
    },
  },
};
